import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  BedDouble,
  ChevronRight,
  Clock,
  Dumbbell,
  GraduationCap,
  Layers,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  Moon,
  Plus,
  ScanLine,
  Sparkles,
  UserRound,
  Eye,
  Waves,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/blepharoplasty-eyelid-surgery-mumbai";

const BLEPHAROPLASTY_FAQS = [
  {
    question: "What is the difference between upper and lower blepharoplasty?",
    answer:
      "Upper blepharoplasty removes excess skin from the upper eyelid to correct drooping and restore a defined eyelid crease. Lower blepharoplasty removes or repositions fat beneath the lower eyelid to reduce under-eye bags and smooth the under-eye contour. Both are frequently performed together.",
  },
  {
    question: "Will blepharoplasty remove dark circles?",
    answer:
      "It depends on the cause. Dark circles from under-eye bags and shadow cast by the fat pocket improve significantly after lower blepharoplasty. Dark circles caused by pigmentation or thin skin are a separate concern not addressed by surgery alone.",
  },
  {
    question: "How long does blepharoplasty recovery take?",
    answer:
      "Most patients return to desk work within 7 to 10 days. Bruising and swelling are presentable with makeup by week 2. Final results are visible at 2 to 3 months as residual swelling fully resolves.",
  },
  {
    question: "Are blepharoplasty results permanent?",
    answer:
      "Upper eyelid results are long-lasting, typically 8 to 12 years, before additional skin laxity develops with continued aging. Lower eyelid fat removal is permanent. The aging process continues after surgery, but from a corrected baseline.",
  },
  {
    question: "Will there be visible scarring after eyelid surgery?",
    answer:
      "Upper eyelid scars sit within the natural eyelid crease and are not visible when the eye is open. Lower eyelid scars from the subciliary approach run along the lash line and fade to near-invisible within 6 months. The transconjunctival approach leaves no external scar at all.",
  },
  {
    question: "Is blepharoplasty painful?",
    answer:
      "Surgery is performed under anesthesia. Post-operative discomfort is mild, described by most patients as tightness and sensitivity rather than pain. Eye drops and standard medication manage this during the first few days.",
  },
  {
    question: "Can blepharoplasty be combined with other procedures?",
    answer:
      "Yes. Common combinations include fat grafting to the tear trough, facelift for patients who want complete facial rejuvenation, and laser resurfacing to improve lower eyelid skin texture. Dr. Jain recommends combinations based on your specific anatomy during consultation.",
  },
  {
    question: "Can blepharoplasty improve vision?",
    answer:
      "Yes, in cases where excess upper eyelid skin droops into the upper visual field. Upper blepharoplasty removes this obstruction and is considered a functional procedure in these cases, distinct from its cosmetic application.",
  },
];

const blepharoplastyStructuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalProcedure",
      name: "Blepharoplasty (eyelid surgery)",
      alternateName: ["Upper blepharoplasty", "Lower blepharoplasty", "Eyelid surgery"],
      description:
        "Surgical correction of the upper and/or lower eyelids to remove excess skin, reposition or remove fat, and restore eyelid contour. Cosmetic blepharoplasty addresses appearance; when excess upper eyelid skin obstructs the visual field, upper blepharoplasty may be performed as a functional procedure—clinically and for insurance purposes this is distinct from purely cosmetic eyelid surgery.",
      bodyLocation: {
        "@type": "AnatomicalStructure",
        name: "Eyelids",
      },
      followup:
        "Days 1–3: swelling and bruising around the eyes, peaking at 48 hours. Days 5–7: sutures removed; bruising fades. Weeks 1–2: most return to desk work. By 2–3 months: residual swelling resolves; final assessment at 3 months.",
      howPerformed:
        "Upper: incision in the natural crease, measured skin (and fat) removal, fine suture closure. Lower: subciliary or transconjunctival approach for fat and/or skin correction; typically 45–90 minutes per area under local with sedation or general anesthesia.",
      url: PAGE_URL,
    },
    {
      "@type": "FAQPage",
      mainEntity: BLEPHAROPLASTY_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    },
  ],
};

function BlepharoplastyJsonLd() {
  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(blepharoplastyStructuredData) }} />
  );
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Cosmetic Surgery", href: "/#services" },
    { name: "Face", href: "/#services" },
    { name: "Blepharoplasty", href: "/blepharoplasty-eyelid-surgery-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Cosmetic Surgery · Face</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">
              Blepharoplasty
            </h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
              Upper &amp; Lower Eyelid Surgery by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">Board Certified</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">17+ Years Experience</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">International Training</span>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Blepharoplasty" icon="👁" size="hero"  imageSrc={HERO.blepharoplasty} imageAlt="Blepharoplasty"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">What is Blepharoplasty?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Blepharoplasty is surgical correction of the eyelids. It removes excess skin, repositions or removes fat
              deposits, and restores the eyelid contour that makes eyes look rested, open, and younger. The eyes are the
              focal point of the face and the first feature to show structural aging. When that structure changes, the
              entire face reads as tired, stressed, or older than it is.
            </p>
            <p>
              No topical product removes excess eyelid skin or repositions herniated fat. Once the structural change has
              occurred, surgery is the only effective correction. Dr. Vicky Jain performs blepharoplasty on the
              upper eyelids, lower eyelids, or both, depending on where the concern is and what the anatomy requires.
            </p>
            <p>
              Upper blepharoplasty addresses drooping skin that weighs down the eyelid and in severe cases obstructs the
              upper visual field. Lower blepharoplasty addresses the fat pockets that create under-eye bags and the loose
              skin that wrinkles the under-eye area. Both procedures are frequently performed together for complete eye
              rejuvenation.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Our Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">
              Dr. Jain&apos;s Approach to Eyelid Surgery
            </h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                Dr. Jain evaluates each eyelid individually before deciding on technique. Upper eyelid surgery requires
                precise measurement of how much skin to remove. Too little and the result is insufficient. Too much and the
                eye cannot close fully. The incision is placed in the natural upper eyelid crease so the scar is invisible
                when the eye is open.
              </p>
              <p>
                Lower eyelid surgery requires a decision between two approaches: an external incision just below the lash
                line, or a transconjunctival approach through the inner surface of the eyelid with no external scar. The
                right choice depends on whether skin removal is needed alongside fat correction. Dr. Jain selects the
                technique based on your anatomy and what the eyelid requires, not a standard default.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function BlepharoplastyProblemsSection() {
  const problems = [
    {
      title: "Droopy Upper Eyelids",
      icon: Eye,
      copy: "Excess skin accumulates on the upper eyelid with age, creating a hooded appearance that makes eyes look smaller and heavier. Upper blepharoplasty removes this skin and restores a defined eyelid contour.",
    },
    {
      title: "Under-Eye Bags",
      icon: Layers,
      copy: "Fat pockets beneath the lower eyelid herniate forward with age, creating the bulge known as under-eye bags. Lower blepharoplasty removes or redistributes this fat to smooth the under-eye contour.",
    },
    {
      title: "Tired or Sleepy Appearance",
      icon: Moon,
      copy: "Heavy upper eyelids and puffy lower eyelids together create a fatigued look that persists regardless of how much sleep you get. Correcting both restores an alert, rested appearance at rest.",
    },
    {
      title: "Wrinkled Under-Eye Skin",
      icon: Waves,
      copy: "Loose lower eyelid skin creates fine wrinkling and crepey texture beneath the eye. Lower blepharoplasty removes the excess skin responsible for this alongside fat correction.",
    },
    {
      title: "Vision Obstruction",
      icon: ScanLine,
      copy: "In significant upper eyelid ptosis, the excess skin droops far enough to obstruct the upper visual field. Upper blepharoplasty corrects this functional problem alongside the cosmetic improvement.",
    },
    {
      title: "Eyes That Look Older Than Your Face",
      icon: UserRound,
      copy: "When eyelid aging outpaces the rest of the face, the eyes become the dominant aging signal. Blepharoplasty brings the eyes back into proportion with the rest of your appearance.",
    },
  ];

  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="blepharoplasty-problems-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Indications</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">What Blepharoplasty Corrects</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {problems.map((card, index) => (
            <div
              key={card.title}
              className="bg-white p-8 border border-[#E8E2D9]"
              data-testid={`blepharoplasty-problem-card-${index}`}
            >
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function EyelidTypesSection() {
  const types = [
    {
      title: "Upper Blepharoplasty",
      copy: "An incision is made in the natural crease of the upper eyelid, excess skin is removed, and the incision is closed with fine sutures. The scar sits within the crease and is not visible when the eye is open. Surgery takes 45 to 60 minutes. Local anesthesia with sedation is sufficient in most cases.",
    },
    {
      title: "Lower Blepharoplasty",
      copy: "Addresses fat pockets and loose skin beneath the eye. Two incision approaches are used depending on what the lower eyelid requires. The subciliary approach places the incision just below the lash line and allows skin removal alongside fat correction. The transconjunctival approach goes through the inner surface of the lower eyelid with no external scar, used when fat removal alone is needed and skin quality is good.",
    },
    {
      title: "Combined Upper and Lower Blepharoplasty",
      copy: "Both eyelids are addressed in a single procedure for patients with concerns in both areas. This is the most common approach for complete eye rejuvenation and avoids a second surgery.",
    },
    {
      title: "Combination with Other Procedures",
      copy: "Blepharoplasty is frequently combined with fat grafting to the tear trough to address the hollow beneath the eye bag, laser resurfacing to improve lower eyelid skin texture, and facelift for patients who want complete facial rejuvenation. Dr. Jain discusses which combinations apply to your specific anatomy during consultation.",
    },
  ];

  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="eyelid-types-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Procedure Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-10 text-center">Types of Eyelid Surgery</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {types.map((item, index) => (
            <div
              key={item.title}
              className="bg-white p-8 border border-[#E8E2D9]"
              data-testid={`eyelid-type-card-${index}`}
            >
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{item.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowSurgeryPerformedSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-surgery-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">How Blepharoplasty Surgery is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Upper blepharoplasty takes 45 to 60 minutes under local anesthesia with sedation or general anesthesia. Dr.
              Jain marks the upper eyelid crease precisely before surgery, makes the incision within the crease, removes
              the measured amount of skin and any excess fat, and closes with fine sutures. The incision is hidden in the
              crease when the eye is open.
            </p>
            <p>
              Lower blepharoplasty takes 60 to 90 minutes depending on technique and whether both skin and fat are
              addressed. For the subciliary approach, the incision runs just below the lower lash line. Fat is removed or
              repositioned, excess skin is trimmed, and closure is done with fine sutures that are removed within 5 to 7
              days. For the transconjunctival approach, the incision is made inside the lower eyelid and no external sutures
              are required.
            </p>
            <p>
              Most patients go home the same day. Cold compresses, head elevation, and avoiding eye strain are the main
              requirements during the first 48 hours.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Days 1 to 3",
      description: "Swelling and bruising around both eyes. This is normal and peaks at 48 hours before beginning to reduce.",
      icon: BedDouble,
    },
    {
      period: "Days 5 to 7",
      description: "Sutures are removed. Bruising fades from dark to yellow. Most patients can go out with sunglasses.",
      icon: Monitor,
    },
    {
      period: "Weeks 1 to 2",
      description: "Most patients return to desk work. Swelling continues to reduce. Eyes look noticeably more rested.",
      icon: Dumbbell,
    },
    {
      period: "Weeks 3 to 4 and Beyond",
      description:
        "The eyelids settle into their final position. Residual swelling resolves fully by month 2 to 3. Final results are assessed at 3 months.",
      icon: Sparkles,
    },
  ];

  return (
    <section className="bg-premium-purple py-14 md:py-20" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">What to Expect</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function WhoIsGoodCandidateSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="who-is-candidate-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Am I a Candidate?</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">
              Who Is a Good Candidate for Blepharoplasty?
            </h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Most patients are between 35 and 65, though younger patients with genetic predisposition to under-eye bags
              or heavy upper eyelids are good candidates regardless of age. The deciding factor is the structural concern,
              not age alone. Patients in their 30s with significant under-eye fat herniation benefit from early correction
              before surrounding tissue changes compound the problem.
            </p>
            <p>
              Good candidates are in good general health, do not have dry eye conditions that surgery could worsen, and have
              specific concerns with their upper or lower eyelids. Patients who are regularly asked if they are tired,
              notice their eyes look smaller or heavier than they used to, or see persistent puffiness that does not
              respond to sleep or lifestyle changes are typical candidates. Patients with vision obstruction from upper
              eyelid skin have both a cosmetic and functional reason for surgery.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PatientResultsSection() {
  return <PatientTestimonials procedureName="Blepharoplasty" />;
}

function FAQSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {BLEPHAROPLASTY_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`item-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">
          Book Your Blepharoplasty Consultation
        </h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain examines your eyelid skin laxity, fat distribution, eyelid position, and tear
          trough depth to determine whether upper, lower, or combined surgery is appropriate and which technique your
          anatomy requires. You leave with a precise surgical plan, realistic outcome expectations, and a clear picture of
          what recovery involves.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Facelift",
      description: "Complete facial rejuvenation to complement eyelid correction.",
      href: "/facelift-surgery-mumbai",
    },
    {
      name: "Cheek Augmentation",
      description: "Restore mid-face volume to address the tear trough and under-eye hollow.",
      href: "/cheek-augmentation-in-mumbai",
    },
    {
      name: "Thread Lift",
      description: "Non-surgical lifting option for patients with early facial laxity alongside eyelid concerns.",
      href: "/thread-lift-mumbai",
    },
  ];

  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function BlepharoplastyPage() {
  return (
    <>
      <SEO
        title="Blepharoplasty | Eyelid Surgery by Dr. Vicky Jain"
        description="Droopy eyelids and under-eye bags make you look tired even when you're not. Dr. Vicky Jain performs eyelid surgery with natural results. Book today."
        canonical="/blepharoplasty-eyelid-surgery-mumbai"
      />
      <BlepharoplastyJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <BlepharoplastyProblemsSection />
      <EyelidTypesSection />
      <HowSurgeryPerformedSection />
      <RecoveryTimelineSection />
      <WhoIsGoodCandidateSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
